import { toast } from '@/lib/hooks/use-toast'
import { NON_QUOTE_EDIT_MUTATION_KEY_ROOT } from '@/lib/quote-activation-gate'
import { trackException } from '@/lib/telemetry'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { proposalApi } from './client'
import { getCurrentUserId } from './emails'
import { createRecord, deleteRecord, getMultipleRecords, updateRecord } from './helpers'
import { QUERY_KEYS } from './query-keys'
import type { QuoteDetailData } from './quote-details'
import { quoteDetailSchema } from './quote-details'
import { useQuoteQuery } from './quotes'
import { getSystemLabel, getSystemScopeTagLabel } from './rooms-and-systems'

export const templateSchema = z.object({
  dsy_quotedetailtemplateid: z.string({ message: 'Template ID is missing.' }),
  dsy_name: z.string({ message: 'Template name is missing.' }),
  dsy_isshared: z.boolean({ message: 'Template shared flag is missing.' }),
  dsy_templatetype: z.number({ message: 'Template type is missing.' }),
  dsy_items: z.string({ message: 'Template items data is missing.' }),
  _dsy_pricelevel_value: z.string().nullable(),
  _ownerid_value: z.string({ message: 'Template owner is missing.' }),
  createdon: z.string({ message: 'Template creation date is missing.' }),
  dsy_sortorder: z.number().nullable(),
})

export type TemplateData = z.infer<typeof templateSchema>

export const TemplateType = {
  Room: 309890000,
  System: 309890001,
} as const

export function parseTemplateItems(template: TemplateData) {
  try {
    const parsed = JSON.parse(template.dsy_items)
    return z.array(quoteDetailSchema).parse(parsed)
  } catch (error) {
    trackException(error, { context: 'templates.parseTemplateItems' })
    toast({
      title: 'Failed to parse template items',
      variant: 'destructive',
    })
    return []
  }
}

export function useTemplatesQuery() {
  const { data: quote } = useQuoteQuery()
  const priceListId = quote?._pricelevelid_value

  return useQuery({
    queryKey: QUERY_KEYS.templates({ priceListId }),
    enabled: !!priceListId,
    queryFn: async () => {
      if (!priceListId) throw new Error('Price list not found on quote')

      const userId = await getCurrentUserId()

      const templates = await getMultipleRecords<TemplateData>(
        'dsy_quotedetailtemplates',
        `?$select=dsy_quotedetailtemplateid,dsy_name,dsy_isshared,dsy_templatetype,dsy_items,_dsy_pricelevel_value,_ownerid_value,createdon,dsy_sortorder` +
          `&$filter=_dsy_pricelevel_value eq '${priceListId}' and (_ownerid_value eq '${userId}' or dsy_isshared eq true)` +
          `&$orderby=dsy_sortorder asc,createdon desc`
      )

      return {
        templates: z.array(templateSchema).parse(templates),
        currentUserId: userId,
      }
    },
  })
}

export function useCreateTemplate() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()

  return useMutation({
    mutationKey: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'create-template'],
    mutationFn: async ({
      name,
      isShared,
      templateType,
      items,
      sortOrder,
    }: {
      name: string
      isShared: boolean
      templateType: number
      items: unknown[]
      sortOrder?: number
    }) => {
      const priceListId = quote?._pricelevelid_value
      if (!priceListId) throw new Error('Price list not found on quote')

      return createRecord(
        'dsy_quotedetailtemplates',
        {
          dsy_name: name,
          dsy_isshared: isShared,
          dsy_templatetype: templateType,
          dsy_items: JSON.stringify(items),
          'dsy_pricelevel@odata.bind': `/pricelevels(${priceListId})`,
          ...(sortOrder !== undefined && { dsy_sortorder: sortOrder }),
        },
        '',
        true
      )
    },
    onSuccess: () => {
      toast({ title: 'Template saved' })
    },
    onError: (error) => {
      toast({ title: 'Error saving template', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates({ priceListId: quote?._pricelevelid_value }),
      })
    },
  })
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()

  return useMutation({
    mutationKey: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'update-template'],
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      dsy_name?: string
      dsy_isshared?: boolean
      dsy_items?: string
      dsy_sortorder?: number
    }) => {
      return updateRecord('dsy_quotedetailtemplates', id, body, true)
    },
    onSuccess: () => {
      toast({ title: 'Template updated' })
    },
    onError: (error) => {
      toast({ title: 'Error updating template', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates({ priceListId: quote?._pricelevelid_value }),
      })
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()

  return useMutation({
    mutationKey: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'delete-template'],
    mutationFn: async (id: string) => {
      return deleteRecord('dsy_quotedetailtemplates', id, true)
    },
    onSuccess: () => {
      toast({ title: 'Template deleted' })
    },
    onError: (error) => {
      toast({ title: 'Error deleting template', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates({ priceListId: quote?._pricelevelid_value }),
      })
    },
  })
}

export function useReorderTemplates() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()

  return useMutation({
    mutationKey: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'reorder-templates'],
    mutationFn: async (updates: { id: string; dsy_sortorder: number }[]) => {
      await Promise.all(
        updates.map(({ id, dsy_sortorder }) =>
          updateRecord('dsy_quotedetailtemplates', id, { dsy_sortorder }, true)
        )
      )
    },
    onMutate: async (updates) => {
      const queryKey = QUERY_KEYS.templates({ priceListId: quote?._pricelevelid_value })
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, (old: { templates: TemplateData[]; currentUserId: string } | undefined) => {
        if (!old) return old
        const updateMap = new Map(updates.map((u) => [u.id, u.dsy_sortorder]))
        const updatedTemplates = old.templates.map((t) =>
          updateMap.has(t.dsy_quotedetailtemplateid)
            ? { ...t, dsy_sortorder: updateMap.get(t.dsy_quotedetailtemplateid)! }
            : t
        )
        updatedTemplates.sort((a, b) => (a.dsy_sortorder ?? Infinity) - (b.dsy_sortorder ?? Infinity))
        return { ...old, templates: updatedTemplates }
      })

      return { previous }
    },
    onError: (error, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          QUERY_KEYS.templates({ priceListId: quote?._pricelevelid_value }),
          context.previous
        )
      }
      toast({ title: 'Error reordering templates', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates({ priceListId: quote?._pricelevelid_value }),
      })
    },
  })
}

export function useGenerateTemplateName() {
  return useMutation({
    mutationKey: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'generate-template-name'],
    mutationFn: async (items: QuoteDetailData[]) => {
      const payload = items
        .filter((item) => !item.dsy_cpqfields.dsy_parentglobalid)
        .map((item) => ({
          productName: (item.productid.dsy_ispackage ? item.quotedetailname : item.productid.name),
          quantity: item.quantity,
          system: getSystemLabel(item.dsy_cpqfields.dsy_system),
          scopeTag: getSystemScopeTagLabel(item.dsy_cpqfields.dsy_scopetag),
        }))

      const res = await proposalApi.post<string>('/ai_template_name', payload)
      return res.data
    },
    onError: (error) => {
      toast({ title: 'Error generating template name', error, variant: 'destructive' })
    },
  })
}
